import {
  Accordion,
  AccordionItem,
  Checkbox,
  Column,
  Link,
  Row,
  Toggle,
  Tooltip,
} from 'carbon-components-react';
import PublicImage from 'src/common/components/PublicImage';
import '../Assessments/index.scss';
import { useEffect, useState } from 'react';
import { StepProps } from '../../../lib/interfaces';
import { defaultValues, segmentsTrack } from '../../../constants/default';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import { ChevronDown16, ChevronUp16 } from '@carbon/icons-react';

type FeatureFlags = {
  [key: string]: boolean;
};

const initialFeatureFlags = {
  snapshotManagement: true,
  replication: true,
  disasterRecovery: false,
  highAvailability: false,
  backupAndRecovery: true,
};

const initialCyberFlags = {
  immutableCopies: true,
  inlineThreat: true,
  airGapped: true,
  regulatoryRequirements: false,
  cyberRecovery: false,
};

const Step2 = ({ postObj, setPostObj, useTranslation }: StepProps) => {
  const businessCyberResilience =
    defaultValues('businessCyberResilience', useTranslation) || [];
  const businessContinuityItems =
    defaultValues('businessContinuity', useTranslation) || [];
  const cyberResilienceItems =
    defaultValues('cyberResilience', useTranslation) || [];
  const businessObj = Object.assign({}, initialFeatureFlags);
  const cyberObj = Object.assign({}, initialCyberFlags);
  const [businessContinuity, setBusinessContinuity] =
    useState<FeatureFlags>(businessObj);
  const [cyberResilience, setCyberResilience] =
    useState<FeatureFlags>(cyberObj);
  const [openIndexFirst, setOpenIndexFirst] = useState(true);
  const [openIndexSecond, setOpenIndexSecond] = useState(true);
  const [isChecked, setIsChecked] = useState(true);
  const [isCheckedCyber, setIsCheckedCyber] = useState(true);
  const isSmall = useSmallBreakpoint('560px');

  const hasChangedCheck = (type: string, stateObj: any) => {
    const defaultObj =
      type === 'business' ? initialFeatureFlags : initialCyberFlags;
    return Object.keys(stateObj).some((key) => {
      return (
        stateObj[key as keyof typeof defaultObj] !==
        defaultObj[key as keyof typeof defaultObj]
      );
    });
  };
  const handleContinuityChange = async (
    key: string,
    value: boolean,
    type: string,
  ) => {
    if (type === 'business') {
      businessContinuity[key] = value;
      setBusinessContinuity(businessContinuity);
      await setBusinessContinuity((prevFlags) => {
        const updatedFlags = { ...prevFlags, [key]: value };
        setIsChecked(!hasChangedCheck(type, updatedFlags));
        return updatedFlags;
      });
    }
    if (type === 'cyber') {
      cyberResilience[key] = value;
      setCyberResilience(cyberResilience);
      await setCyberResilience((prevFlags) => {
        const updatedFlags = { ...prevFlags, [key]: value };
        setIsCheckedCyber(!hasChangedCheck(type, updatedFlags));
        return updatedFlags;
      });
    }
  };
  const onChangeBusiness = (bool: boolean, type: string) => {
    if (type === 'business') {
      setIsChecked(bool);
      if (!!bool)
        setBusinessContinuity((prevFlags) => {
          return { ...prevFlags, ...initialFeatureFlags };
        });
      else
        setBusinessContinuity((prevFlags) => {
          return {
            ...prevFlags,
            ...initialFeatureFlags,
            ...{
              snapshotManagement: false,
              replication: false,
              backupAndRecovery: false,
            },
          };
        });
    }

    if (type === 'cyber') {
      setIsCheckedCyber(bool);
      if (!!bool) {
        setCyberResilience((prevFlags) => {
          return { ...prevFlags, ...initialCyberFlags };
        });
      } else {
        setCyberResilience((prevFlags) => {
          return {
            ...prevFlags,
            ...initialCyberFlags,
            ...{
              immutableCopies: false,
              inlineThreat: false,
              airGapped: false,
            },
          };
        });
      }
    }
    segment('Default Option Selected');
  };

  const _triggerUpdate = () =>
    setPostObj((prevFlags: any) => {
      const updatedFlags = Object.assign(
        {},
        prevFlags,
        businessContinuity,
        cyberResilience,
      );
      return updatedFlags;
    });

  useEffect(() => {
    _triggerUpdate();
  }, [businessContinuity, cyberResilience]);

  useEffect(() => {
    Object.keys(postObj).map(async (key) => {
      if (businessContinuity.hasOwnProperty(key)) {
        businessContinuity[key] = postObj[key];
        await setBusinessContinuity((prevFlags) => {
          const updatedFlags = { ...prevFlags, [key]: postObj[key] };
          setIsChecked(!hasChangedCheck('business', updatedFlags));
          return updatedFlags;
        });
      }
      if (cyberResilience.hasOwnProperty(key)) {
        cyberResilience[key] = postObj[key];
        await setCyberResilience((prevFlags) => {
          const updatedFlags = { ...prevFlags, [key]: postObj[key] };
          setIsCheckedCyber(!hasChangedCheck('cyber', updatedFlags));
          return updatedFlags;
        });
      }
    });
    _triggerUpdate();
  }, []);

  const segment = (evtKey = '') => {
    segmentsTrack(evtKey, 'Assessment Step 2 Page');
  };

  useEffect(() => {
    if (!!openIndexFirst && !!openIndexSecond) setExpandAll(true);
    if (!openIndexFirst && !openIndexSecond) setExpandAll(false);
  }, [openIndexFirst, openIndexSecond]);

  const firstContent: any = businessCyberResilience[1];
  const secondContent: any = businessCyberResilience[2];
  const [expandAll, setExpandAll] = useState(true);

  const _triggerAccordian = (): void => {
    setOpenIndexSecond(!openIndexSecond);
  };

  const _triggerAccordianLink = (): void => {
    setOpenIndexFirst(!expandAll);
    setOpenIndexSecond(!expandAll);
    setExpandAll(!expandAll);
    segment(expandAll === true ? 'Collapse' : 'Expand' + ' All click');
  };

  return (
    <div className="bx--row fixHeight">
      <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-16 bx--col-padding border-lr p-tb-0">
        <table className="fixHeight">
          <tbody>
            <tr>
              <td className="table-td-desc slide_text bx--col-padding p-b-0">
                <span>{useTranslation('step2_para')}</span>
              </td>
              {!isSmall && (
                <td rowSpan={openIndexFirst || openIndexSecond ? 1 : 2}>
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 bx--col-padding p-t-a p-b-0">
                    <PublicImage
                      imgPath={'images/business--continuity.svg'}
                      widgetId="solution-advisor"
                      width="100%"
                      height="100%"
                      className={
                        openIndexFirst || openIndexSecond
                          ? 'image-fixed screen'
                          : 'screen'
                      }
                      alt="animation-image"
                    />
                  </div>
                </td>
              )}
            </tr>
            {isSmall && (
              <tr>
                <td className="h-0">
                  <div className="bx--col-md-8 bx--col-sm-4">
                    <PublicImage
                      imgPath={'images/business--continuity.svg'}
                      widgetId="solution-advisor"
                      width="100%"
                      height="100%"
                      alt="animation-image"
                      className="screen small-screen"
                    />
                  </div>
                </td>
              </tr>
            )}
            <tr>
              <td className={!isSmall ? 'b-r-1' : ''}>
                <Link
                  href="javascript:;"
                  className="link-accordian"
                  onClick={() => _triggerAccordianLink()}
                >
                  {!!expandAll
                    ? useTranslation('step2_collapse_all')
                    : useTranslation('step2_expand_all')}
                  {!!expandAll ? (
                    <ChevronUp16 className="m-l-6" />
                  ) : (
                    <ChevronDown16 className="m-l-6" />
                  )}
                </Link>
                <Accordion
                  size="sm"
                  className="interactive-widget__accordion p-t-accordian float-lg-start"
                >
                  <AccordionItem
                    title={useTranslation('step2_accordion_title1')}
                    onHeadingClick={(data) => {
                      setOpenIndexFirst(openIndexFirst === true ? false : true);
                      segment('Accordian heading click');
                    }}
                    open={openIndexFirst}
                    className="p-b-0"
                  >
                    <div className="b-b-1">
                      {businessContinuityItems.map((business: any, index) => (
                        <Row key={index}>
                          <Column
                            sm={3}
                            md={6}
                            lg={12}
                            className="align-hcenter"
                          >
                            <span>{business.text}</span>
                          </Column>
                          <Column sm={1} md={2} lg={4} className="p-l-0 p-lr-0">
                            {isSmall && (
                              <div className="tooltip-div">
                                <Tooltip
                                  // onChange={() => {
                                  //   segmentTracking('CTA Clicked', {
                                  //     pageTitle: 'watsonx Code Assistant Page',
                                  //     object: 'Pricing page',
                                  //     resultValue: 'CTA clicked',
                                  //     CTA: 'Toggle tooltip',
                                  //     location: 'Estimator',
                                  //     action: '0',
                                  //     field: '0',
                                  //   });
                                  // }}
                                  autoOrientation={true}
                                  direction="right"
                                  tabIndex={0}
                                >
                                  <span className="slide_text">
                                    {firstContent.lists[index]}
                                  </span>
                                </Tooltip>
                              </div>
                            )}
                            <div
                              style={{
                                float: 'right',
                                width: !isSmall ? '100%' : '70%',
                              }}
                            >
                              <Toggle
                                labelA={
                                  useTranslation('step_option_no') as string
                                }
                                labelB={
                                  useTranslation('step_option_yes') as string
                                }
                                id={business.value}
                                toggled={
                                  businessContinuity[
                                    business.value as keyof typeof initialFeatureFlags
                                  ]
                                }
                                size="sm"
                                onChange={() => {
                                  handleContinuityChange(
                                    business.value,
                                    !businessContinuity[business.value],
                                    'business',
                                  );
                                  segment(`${business.text} Selected`);
                                }}
                              />
                            </div>
                          </Column>
                        </Row>
                      ))}
                    </div>
                    {isSmall && (
                      <div className="bx--col-padding disclaimer-height b-b-1">
                        {openIndexFirst &&
                          useTranslation('step2_right_desclaim1')}
                      </div>
                    )}
                    <div className="bx--col-padding disclaimer-height product-desc">
                      <Checkbox
                        id="checkbox_business"
                        labelText={useTranslation('step_recommendation')}
                        checked={isChecked}
                        onChange={() =>
                          onChangeBusiness(!isChecked, 'business')
                        }
                      />
                    </div>
                  </AccordionItem>
                  <AccordionItem
                    title={useTranslation('step2_accordion_title2')}
                    onHeadingClick={(data) => _triggerAccordian()}
                    open={openIndexSecond}
                    className="p-b-0"
                  >
                    <div className="b-b-1">
                      {cyberResilienceItems.map((cyber: any, index) => (
                        <Row key={index}>
                          <Column
                            sm={3}
                            md={6}
                            lg={12}
                            className="align-hcenter"
                          >
                            <span>{cyber.text}</span>
                          </Column>
                          <Column sm={1} md={2} lg={4} className="p-l-0 p-lr-0">
                            {isSmall && (
                              <div className="tooltip-div">
                                <Tooltip
                                  // onChange={() => {
                                  //   segmentTracking('CTA Clicked', {
                                  //     pageTitle: 'watsonx Code Assistant Page',
                                  //     object: 'Pricing page',
                                  //     resultValue: 'CTA clicked',
                                  //     CTA: 'Toggle tooltip',
                                  //     location: 'Estimator',
                                  //     action: '0',
                                  //     field: '0',
                                  //   });
                                  // }}
                                  autoOrientation={true}
                                  direction="right"
                                  tabIndex={0}
                                >
                                  <span className="slide_text">
                                    {secondContent.lists[index]}
                                  </span>
                                </Tooltip>
                              </div>
                            )}
                            <div
                              style={{
                                float: 'right',
                                width: !isSmall ? '100%' : '70%',
                              }}
                            >
                              <Toggle
                                labelA={
                                  useTranslation('step_option_no') as string
                                }
                                labelB={
                                  useTranslation('step_option_yes') as string
                                }
                                id={cyber.value}
                                toggled={
                                  cyberResilience[
                                    cyber.value as keyof typeof initialCyberFlags
                                  ]
                                }
                                size="sm"
                                onChange={() => {
                                  handleContinuityChange(
                                    cyber.value,
                                    !cyberResilience[cyber.value],
                                    'cyber',
                                  );
                                  segment(`${cyber.text} Selected`);
                                }}
                              />
                            </div>
                          </Column>
                        </Row>
                      ))}
                    </div>
                    {isSmall && (
                      <div className="bx--col-padding disclaimer-height b-b-1">
                        {openIndexSecond &&
                          useTranslation('step2_right_desclaim2')}
                      </div>
                    )}
                    <div className="bx--col-padding disclaimer-height product-desc">
                      <Checkbox
                        id="checkbox"
                        labelText={useTranslation('step_recommendation')}
                        checked={isCheckedCyber}
                        onChange={() =>
                          onChangeBusiness(!isCheckedCyber, 'cyber')
                        }
                      />
                    </div>
                  </AccordionItem>
                </Accordion>
              </td>
              {(openIndexFirst || openIndexSecond) && !isSmall && (
                <td className="b-r-1">
                  <Link
                    href="javascript:;"
                    className="link-accordian hide-accordian"
                  >
                    {expandAll === true ? 'Collapse All' : 'Expand All'}
                  </Link>
                  <Accordion
                    size="sm"
                    className="interactive-widget__accordion p-t-accordian float-lg-start"
                  >
                    <AccordionItem
                      title={useTranslation('step2_accordion_title1')}
                      className={
                        openIndexFirst ? 'hide-content' : 'hide-accordian'
                      }
                      open={openIndexFirst}
                    >
                      <div className="b-b-1">
                        {firstContent.lists.map((list: string, key: number) => (
                          <Row key={key}>
                            <Column
                              sm={16}
                              md={8}
                              lg={16}
                              className="align-hcenter"
                            >
                              <span>{list}</span>
                            </Column>
                          </Row>
                        ))}
                      </div>
                      <div className="bx--col-padding disclaimer-height product-desc">
                        {useTranslation('step2_right_desclaim1')}
                      </div>
                    </AccordionItem>
                    <AccordionItem
                      title={useTranslation('step2_accordion_title2')}
                      open={openIndexSecond}
                      className={
                        openIndexSecond ? 'hide-content' : 'hide-accordian'
                      }
                    >
                      <div className="b-b-1">
                        {secondContent.lists.map(
                          (list: string, key: number) => (
                            <Row key={key}>
                              <Column
                                sm={16}
                                md={8}
                                lg={16}
                                className="align-hcenter"
                              >
                                <span>{list}</span>
                              </Column>
                            </Row>
                          ),
                        )}
                      </div>
                      <div className="bx--col-padding disclaimer-height product-desc">
                        {useTranslation('step2_right_desclaim2')}
                      </div>
                    </AccordionItem>
                  </Accordion>
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Step2;
